/**
 * The triage score's reasons as the platform records them (in English, on the case) read in Arabic. Three shapes are written: the
 * structural factors of a concentration ("Combined market share (+30); …"), the evidence strength of an investigation, and the
 * Article 9 scorecard of an exemption (recorded as "Article 7" before the citation was corrected). Null for a rationale in any other
 * shape: an officer wrote it, and it is shown as written.
 */
const FACTORS: Record<string, string> = {
  "Combined market share": "الحصة السوقية المجمعة",
  "Post-transaction HHI": "مؤشر HHI بعد الصفقة",
  "Change in HHI": "التغير في مؤشر HHI",
  "Vertical / conglomerate overlap": "التداخل الرأسي أو التكتلي",
  "Sector sensitivity": "حساسية القطاع",
  "Third-party complaints": "شكاوى الغير",
};

const CONDITIONS: Record<string, string> = {
  "Improves production or distribution, or promotes technical or economic progress": "يحسن الإنتاج أو التوزيع أو يعزز التقدم التقني أو الاقتصادي",
  "Consumers receive a fair share of the resulting benefit": "يحصل المستهلكون على نصيب عادل من المنافع",
  "Restrictions are indispensable to achieving those benefits": "القيود ضرورية لتحقيق تلك المنافع",
  "Does not eliminate competition in a substantial part of the market": "لا يقضي على المنافسة في جزء جوهري من السوق",
  "Necessary to promote economic development, the parties' performance and competitiveness, production or distribution, or benefits to consumers":
    "ضروري لتنمية الاقتصاد أو تحسين أداء المنشآت وقدرتها التنافسية أو تطوير أنظمة الإنتاج أو التوزيع أو تحقيق منافع للمستهلك",
  "The gains are clearly and substantially established and benefit the market as a whole, consumers included":
    "ثبوت المكاسب بشكل واضح وجوهري وعود نفعها على السوق المعنية ككل بما في ذلك المستهلكون",
  "The restrictions go no further than necessary to achieve those aims": "لا تتجاوز القيود ما هو ضروري لتحقيق تلك الأهداف",
  "Competition is not completely eliminated in the relevant market or a significant part of it": "عدم القضاء كليا على المنافسة في السوق المعنية أو في جزء كبير منها",
};

export function riskRationaleText(text: string | null | undefined, locale: string): string | null {
  if (!text) return null;
  if (!locale.startsWith("ar") || !/[A-Za-z]{3,}/.test(text)) return text;

  const parts = text.split(/\s*;\s*/).map((p) => p.match(/^(.+?) \(\+(\d+)\)$/));
  if (parts.every((m) => m && FACTORS[m[1]])) return parts.map((m) => `${FACTORS[m![1]]} (+${m![2]})`).join("؛ ");

  const evidence = text.match(/^Evidence strength (\d+)\/100 \(weighted support ([\d.]+) vs contradiction ([\d.]+) across (\d+) items\)/);
  if (evidence) return `قوة الأدلة ${evidence[1]} من 100 (وزن الأدلة المؤيدة ${evidence[2]} مقابل ${evidence[3]} للأدلة المعارضة، في ${evidence[4]} من البنود)، مدمجة مع أولوية الفرز.`;

  // The published file-risk method's own rationales (condition 2.1).
  const triage = text.match(/^Triage priority (\d+)\/100 as approved by an officer/);
  if (triage) return `أولوية الفرز ${triage[1]} من 100 كما اعتمدها موظف؛ وابتداء من أول دليل تدمج الدرجة لوحة الأدلة معها (المنهجية المنشورة).`;
  const exemption = text.match(/^100 less the overall score (\d+)\/100 of the Article 9 compliance scorecard/);
  if (exemption) return `100 ناقص الدرجة الإجمالية ${exemption[1]} من 100 لبطاقة تقييم التوافق مع المادة 9 التي اعتمدها خبير قانوني (المنهجية المنشورة).`;
  const grievance = text.match(/^The approved grievance review recommends (accepting the grievance in part|accepting the grievance|rejecting the grievance): (\d+)/);
  if (grievance) return `توصي مراجعة التظلم المعتمدة ${grievance[1] === "rejecting the grievance" ? "برفض التظلم" : grievance[1] === "accepting the grievance in part" ? "بقبول التظلم جزئيا" : "بقبول التظلم"}: ${grievance[2]} (المنهجية المنشورة).`;

  const scorecard = text.match(/Article [79] scorecard (\d+)\/100 – weakest condition: (.+?) \((\d+)\)/);
  if (scorecard) {
    const weakest = CONDITIONS[scorecard[2]];
    return `بطاقة الامتثال للمادة 9: ${scorecard[1]} من 100${weakest ? ` – أضعف الشروط: ${weakest} (${scorecard[3]})` : ""}.`;
  }
  return null;
}
